import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import styles from '../components/Application/ProfessorApplications.module.css';

export default function ProfessorApplicationsPage() {
  const { positionId } = useParams();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    
    const fetchPositionAndApplications = async () => {
      setLoading(true);
      try {
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        // First fetch the position details
        const positionResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/positions/${positionId}`,
          config
        );
        setPosition(positionResponse.data);

        // Then fetch applications for this position
        const applicationsResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/applications/position/${positionId}`,
          config
        );
        setApplications(applicationsResponse.data || []);
      } catch (error) {
        console.error('Error fetching position applications:', error);
        if (error.response?.status === 403) {
          alert('You do not have permission to view these applications.');
          navigate('/professor-dashboard');
        } else {
          alert('Error loading applications. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPositionAndApplications();
  }, [positionId, token, navigate]);

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/applications/${applicationId}/status`,
        { status: newStatus },
        config
      );
      
      // Update local state to reflect the change
      setApplications(applications.map(app => 
        app._id === applicationId ? { ...app, status: newStatus } : app
      ));
      
      const statusDisplay = newStatus === 'accepted' ? 'moved forward' : newStatus;
      alert(`Application status updated to ${statusDisplay}`);
    } catch (error) {
      console.error('Error updating application status:', error);
      alert('Failed to update application status');
    }
  };


  const getStatusDisplay = (status) => {
    switch (status) {
      case 'accepted': return 'moved forward';
      case 'rejected': return 'rejected';
      case 'pending': return 'pending';
      default: return status;
    }
  };

  const handleViewDetails = (application) => {
    setSelectedApplication(application);
  };

  const closeDetailsModal = () => {
    setSelectedApplication(null);
  };

  if (loading) {
    return (
      <div className={styles.applicationPage}>
        <div className={styles.applicationLoading}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.applicationPage}>
      <div className={styles.applicationContainer}>
        <div className={styles.applicationHeader}>
          <h1 className={styles.applicationTitle}>Applications</h1>
          {position && (
            <p className={styles.applicationSubtitle}>
              Reviewing applications for: <strong>{position.title}</strong>
            </p>
          )}
        </div>

        {/* Application Stats */}
        <div className={styles.statsContainer}>
          <div className={styles.statCard}>
            <div className={styles.statNumber}>{applications.length}</div>
            <div className={styles.statLabel}>Total Applications</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statNumber} style={{ color: '#f59e0b' }}>
              {applications.filter(app => app.status === 'pending').length}
            </div>
            <div className={styles.statLabel}>Pending</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statNumber} style={{ color: '#22c55e' }}>
              {applications.filter(app => app.status === 'accepted').length}
            </div>
            <div className={styles.statLabel}>Accepted</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statNumber} style={{ color: '#ef4444' }}>
              {applications.filter(app => app.status === 'rejected').length}
            </div>
            <div className={styles.statLabel}>Rejected</div>
          </div>
        </div>

        {/* Applications List */}
        <div className={styles.applicationContent}>
          {applications.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📝</div>
              <h3>No Applications Yet</h3>
              <p>No students have applied to this position yet</p>
              <button
                className={styles.browsePositionsBtn}
                onClick={() => navigate('/professor-positions')}
              >
                Back to My Positions
              </button>
            </div>
          ) : (
            <div className={styles.applicationsTable}>
              <table>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>GRE Scores</th>
                    <th>Applied On</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.map(application => (
                    <tr key={application._id} className={styles.applicationRow}>
                      <td>
                        <div className={styles.studentName}>
                          {application.student.name}
                        </div>
                        <div className={styles.studentEmail}>
                          {application.student.email}
                        </div>
                      </td>
                      <td>
                        <div className={styles.greScores}>
                          <span>Verbal: {application.student.greVerbal || 'N/A'}</span>
                          <span>Quant: {application.student.greQuantitative || 'N/A'}</span>
                          <span>Writing: {application.student.greWriting || 'N/A'}</span>
                        </div>
                      </td>
                      <td>
                        {new Date(application.appliedAt).toLocaleDateString()}
                      </td>
                      <td>
                        <div className={`${styles.status} ${styles[application.status]}`}>
                          {getStatusDisplay(application.status)}
                        </div>
                      </td>
                      <td>
                        <div className={styles.actionButtons}>
                          <button
                            className={styles.viewButton}
                            onClick={() => handleViewDetails(application)}
                          >
                            View Details
                          </button>
                          <div className={styles.statusButtons}>
                            <button
                              className={`${styles.statusButton} ${application.status === 'accepted' ? styles.acceptButtonActive : styles.acceptButton}`}
                              onClick={() => handleStatusChange(application._id, application.status === 'accepted' ? 'pending' : 'accepted')}
                            >
                              {application.status === 'accepted' ? 'Undo Move Forward' : 'Move Forward'}
                            </button>
                            <button
                              className={`${styles.statusButton} ${application.status === 'rejected' ? styles.rejectButtonActive : styles.rejectButton}`}
                              onClick={() => handleStatusChange(application._id, application.status === 'rejected' ? 'pending' : 'rejected')}
                            >
                              {application.status === 'rejected' ? 'Undo Reject' : 'Reject'}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Back button */}
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button
            className={styles.browsePositionsBtn}
            onClick={() => navigate('/professor-positions')}
          >
            Back to My Positions
          </button>
        </div>
      </div>

      {/* Student Application Details Modal */}
      {selectedApplication && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>Application Details</h2>
              <button 
                className={styles.closeButton}
                onClick={closeDetailsModal}
              >
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <h3>Student Information</h3>
              <div className={styles.detailsGrid}>
                <div className={styles.detailItem}>
                  <strong>Name:</strong> {selectedApplication.student.name}
                </div>
                <div className={styles.detailItem}>
                  <strong>Email:</strong> {selectedApplication.student.email}
                </div>
                <div className={styles.detailItem}>
                  <strong>GRE Verbal:</strong> {selectedApplication.student.greVerbal || 'N/A'}
                </div>
                <div className={styles.detailItem}>
                  <strong>GRE Quant:</strong> {selectedApplication.student.greQuantitative || 'N/A'}
                </div>
                <div className={styles.detailItem}>
                  <strong>GRE Writing:</strong> {selectedApplication.student.greWriting || 'N/A'}
                </div>
              </div>

              {selectedApplication.student.education && (
                <>
                  <h3>Education</h3>
                  <div className={styles.educationSection}>
                    {selectedApplication.student.education.map((edu, index) => (
                      <div key={index} className={styles.educationItem}>
                        <div className={styles.schoolName}>{edu.school}</div>
                        <div className={styles.degreeInfo}>{edu.degree} in {edu.field}</div>
                        <div className={styles.eduDates}>
                          {edu.startYear} - {edu.endYear || 'Present'}
                        </div>
                        {edu.gpa && <div className={styles.gpa}>GPA: {edu.gpa}</div>}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {selectedApplication.coverLetter && (
                <>
                  <h3>Cover Letter</h3>
                  <div className={styles.coverLetter}>
                    {selectedApplication.coverLetter}
                  </div>
                </>
              )}

              {selectedApplication.student.interests && (
                <>
                  <h3>Research Interests</h3>
                  <div className={styles.interestsList}>
                    {selectedApplication.student.interests.map((interest, index) => (
                      <div key={index} className={styles.interestBadge}>
                        {interest}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {selectedApplication.student.linkedinUrl && (
                <div className={styles.externalLinks}>
                  <a 
                    href={selectedApplication.student.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.linkedinLink}
                  >
                    LinkedIn Profile
                  </a>
                </div>
              )}

              {/* Resume download link if available */}
              {selectedApplication.student.resumeKey && (
                <div className={styles.resumeDownload}>
                  <a 
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/users/resume/${selectedApplication.student.resumeKey}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.resumeLink}
                  >
                    Download Resume
                  </a>
                </div>
              )}

              <div className={styles.statusUpdateSection}>
                <h3>Update Status</h3>
                <div className={styles.statusButtonsLarge}>
                  <button
                    className={`${styles.statusButtonLarge} ${styles.acceptButtonLarge}`}
                    onClick={() => {
                      handleStatusChange(selectedApplication._id, 'accepted');
                      closeDetailsModal();
                    }}
                    disabled={selectedApplication.status === 'accepted'}
                  >
                    Accept Application
                  </button>
                  <button
                    className={`${styles.statusButtonLarge} ${styles.rejectButtonLarge}`}
                    onClick={() => {
                      handleStatusChange(selectedApplication._id, 'rejected');
                      closeDetailsModal();
                    }}
                    disabled={selectedApplication.status === 'rejected'}
                  >
                    Reject Application
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}